import { DropdownButtonType,List, ListItem } from '@tableau/widgets';
import { Formik,FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';
import { FormikSingleSelectDropdown } from '../../commonInfra/components/FormikSingleSelectDropdown';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { LocalesInstance } from '../../ts/resources/Locales';
import { ILocale } from '../../ts/ServerApi/types/ILocale';
import { UserSettingsNotification,UserSettingsNotificationParams, UserSettingsNotificationType } from '../notifications/UserSettingsNotification';
import { UserSettings } from '../services/resources/UserSettings';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';

export interface LocaleFormFields {
  locale: string;
}

export type LocaleFormProps = {
  initialValues: LocaleFormFields;
  serverUser: IServerUserInfo;
}

const userIsViewingTheirOwnSettings = (sessionManagementService: SessionManagementService, serverUser: IServerUserInfo) => {
  return sessionManagementService.sessionUsername() === serverUser.username
    && sessionManagementService.sessionUserDomainName() === serverUser.domainName;
};

const notifyLocaleChange = (result: IResult, username: string) => {
  const userSettingsNotificationParams: UserSettingsNotificationParams = {
    result: result,
    username: username,
    userSettingsNotificationType: UserSettingsNotificationType.ChangeLocale
  };
  UserSettingsNotification.notify(userSettingsNotificationParams);
};

async function showLocaleNotification(result: IResult, serverUser: IServerUserInfo) {
  const sessionManagementService = getSessionManagementServiceInstance();
  if (result && userIsViewingTheirOwnSettings(sessionManagementService, serverUser)) {
    await sessionManagementService.refreshSessionInfo();
    getAppStore().dispatch(incrementRouterSpaceReactKey());
    notifyLocaleChange(result, serverUser.username);
  } else if (result) {
    notifyLocaleChange(result, serverUser.username);
  }
}

function showLocaleButton(formik: FormikProps<LocaleFormFields>, initialLocale: string): boolean {
  if (!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.locale.trim() === initialLocale)) {
    return false;
  }
  return true;
}

const unspecifiedLocaleItemValue = 'UnspecifiedLocale';
export function LocaleForm(props: LocaleFormProps): JSX.Element {
  const [items, setItems] = React.useState([]);
  const [usersCurrentLocale, setUsersCurrentLocale] = React.useState('');

  React.useEffect(() => {
    async function fetchLocales() {
      let locales: ILocale[] = await LocalesInstance.getAll();
      locales.unshift(LocalesInstance.unspecifiedLocale());
      const newItems: ListItem[] = locales.map((locale: ILocale) => ({
        content: locale.displayName,
        value: locale.locale ? locale.locale : unspecifiedLocaleItemValue,
        testId: locale.locale ? locale.locale : unspecifiedLocaleItemValue
      }));
      setItems(newItems);
    }
    if (items.length === 0) {
      fetchLocales();
    }
  });

  React.useEffect(() => {
    if (items.length > 0) {
      const usersCurrentLocale = props.initialValues.locale ? props.initialValues.locale : unspecifiedLocaleItemValue;
      setUsersCurrentLocale(usersCurrentLocale);
    }
  }, [items.length, props.initialValues.locale]);

  const handleSubmit = React.useCallback(
    async (locale: string, formik: FormikHelpers<LocaleFormFields>) => {
      if (locale !== usersCurrentLocale) {
        let localeToUpdate = locale;
        if (locale === unspecifiedLocaleItemValue) {
          localeToUpdate = null;
        }
        const result: IResult = await UserSettings.updateUserLocale(props.serverUser.id, localeToUpdate);
        showLocaleNotification(result, props.serverUser);
        if (!result || result.errors) {
          formik.resetForm();
        } else {
          setUsersCurrentLocale(locale);
        }
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, setUsersCurrentLocale]
  );

  return (
    <Formik
      initialValues={{ locale: usersCurrentLocale }}
      onSubmit={function(values: LocaleFormFields, formik: FormikHelpers<LocaleFormFields>) {
        handleSubmit(values.locale, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<LocaleFormFields>) => (
        <form onSubmit={formik.handleSubmit}>
          <table>
            <tbody className={UserSettingsStyle.tbody}>
              {/* Locale Section */}
              <tr data-test-id="locale-section">
              <td className={UserSettingsStyle.labelText}>
                  {Messages.locale()}
              </td>
              <td className={UserSettingsStyle.detailsValue}>
                  <FormikSingleSelectDropdown
                      name="locale"
                      list={<List items={items} className={UserSettingsStyle.dropdownList}/>}
                      type={DropdownButtonType.Outline}
                      label={''}
                      className={UserSettingsStyle.dropdown}
                      testId={'locale'}/>
              </td>
              </tr>
              {(showLocaleButton(formik, usersCurrentLocale) &&
                <InlineCancelAndSaveButtons
                testId={'edit-locale'}
                formik={formik}
                />
              )}
            </tbody>
          </table>
          <div>
          </div>
        </form>
      )}
    </Formik>
  );
}
